import { Fragment, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../lib/utils";
import { DataGridEditableCell } from "./DataGridEditableCell";
import { DataGridSelectableCell } from "./DataGridSelectableCell";
import { ExpansionChevron } from "./ExpansionChevron";
import { useDataGridDispatch, useDataGridState } from "./state/DataGridStateProvider";
import type {
	ColumnDef,
	DataGridGridLines,
	DataGridLabelIndent,
	DataGridRow,
	DataGridRowFormat,
	DataGridRowFormatAxes,
} from "./types";
import { gridLineClasses, isEditableColumnDef } from "./types";

type DataGridRowProps<TData> = {
	row: DataGridRow<TData>;
	visibleColumns: ColumnDef<TData>[];
	gridTemplate: string;
	groupBoundaryColumnIds: Set<string>;
	lastColumnId?: string;
	depth?: number;
	gridLines?: DataGridGridLines;
	/** Depth-0 left padding of the sticky label cell. */
	labelIndent?: DataGridLabelIndent;
	/** When true, non-collapsible rows omit the fixed expansion-chevron spacer. */
	hideExpansionSpacer?: boolean;
	/** Minimum height (px) for non-compact rows. Defaults to 40; lower it for dense grids. */
	rowHeight?: number;
};

const DEFAULT_ROW_HEIGHT = 40;
const COMPACT_ROW_HEIGHT = 16;

const EMPTY_FORMAT: DataGridRowFormat = {};

const SURFACE_BG = {
	base: "bg-surface-base",
	light: "bg-surface-light",
} as const;

const WEIGHT = {
	normal: "font-normal",
	light: "font-light",
	semibold: "font-semibold",
} as const;

const TONE = {
	primary: "text-primary",
	secondary: "text-secondary",
	tertiary: "text-tertiary",
} as const;

const axesToClasses = (axes: DataGridRowFormatAxes): string =>
	cn(
		axes.weight && WEIGHT[axes.weight],
		axes.tone && TONE[axes.tone],
		axes.labelStyle === "caption" && "text-xs uppercase tracking-wide",
		axes.italic && "italic",
	);

export function DataGridRowComponent<TData>({
	row,
	visibleColumns,
	gridTemplate,
	groupBoundaryColumnIds,
	lastColumnId,
	depth = 0,
	gridLines = "default",
	labelIndent,
	hideExpansionSpacer = false,
	rowHeight = DEFAULT_ROW_HEIGHT,
}: DataGridRowProps<TData>) {
	const state = useDataGridState();
	const dispatch = useDataGridDispatch();

	const { innerBorder, groupBorder, cellBorderClass } = gridLineClasses(
		gridLines,
		groupBoundaryColumnIds,
		lastColumnId,
	);

	const {
		surface = "base",
		density = "default",
		topBorder,
		label: labelAxes,
		cells: cellAxes,
		...baseAxes
	} = row.format ?? EMPTY_FORMAT;
	const labelTextClasses = axesToClasses({ ...baseAxes, ...labelAxes });
	const cellTextClasses = axesToClasses({ ...baseAxes, ...cellAxes });
	const cellClassName = (colId: string) =>
		cellTextClasses ? cn(cellBorderClass(colId), cellTextClasses) : cellBorderClass(colId);

	const hasChildren = !!row.children && row.children.length > 0;
	const isCollapsible = hasChildren && !row.alwaysExpanded;
	const expanded = row.alwaysExpanded ? hasChildren : state.expanded.has(row.id);
	const showValuesOnRow = !(row.hideValuesWhenExpanded && expanded);

	const minHeight = density === "compact" ? COMPACT_ROW_HEIGHT : rowHeight;
	const indentByDepth = ["pl-4", "pl-8", "pl-12", "pl-16"] as const;
	const effectiveDepth = Math.max(0, depth + (row.indentOffset ?? 0));
	const indent =
		effectiveDepth === 0 && labelIndent === "compact"
			? "pl-2"
			: indentByDepth[Math.min(effectiveDepth, indentByDepth.length - 1)];

	const handleRowKeyDown = isCollapsible
		? (e: KeyboardEvent<HTMLDivElement>) => {
				if (e.key === "Enter") {
					e.preventDefault();
					dispatch({ type: "toggle_row", rowId: row.id });
				}
			}
		: undefined;

	const wrapHiddenValues = (content: ReactNode) =>
		showValuesOnRow ? content : <div className="invisible">{content}</div>;

	const cellContent = row.renderCells
		? row.renderCells()
		: visibleColumns.map((col) => {
				if (isEditableColumnDef(col)) {
					const active = typeof col.editable === "function" ? col.editable(row) : true;
					if (active) {
						return (
							<DataGridEditableCell
								key={col.id}
								rowId={row.id}
								colId={col.id}
								row={row}
								renderDisplay={(dataRow) => wrapHiddenValues(col.renderCell(dataRow))}
								getEditValue={col.getEditValue}
								onCommit={col.onCommit}
								onCancel={col.onCancel}
								inputPlaceholder={col.inputPlaceholder}
								inputMode={col.inputMode}
								className={cellClassName(col.id)}
							/>
						);
					}
				}
				return (
					<DataGridSelectableCell
						key={col.id}
						rowId={row.id}
						colId={col.id}
						className={cellClassName(col.id)}
					>
						{wrapHiddenValues(col.renderCell(row))}
					</DataGridSelectableCell>
				);
			});

	const rowBorderClass = row.hasError
		? "border border-danger"
		: cn("border-b", innerBorder, topBorder && "border-t border-bc-default");

	const renderRow = (
		<div
			className={cn("grid w-max min-w-full", rowBorderClass, SURFACE_BG[surface], "group")}
			style={{ gridTemplateColumns: gridTemplate, minHeight: `${minHeight}px` }}
			onKeyDown={handleRowKeyDown}
			data-row-id={row.id}
			data-row-weight={baseAxes.weight}
			data-row-surface={row.format?.surface}
			data-row-density={row.format?.density}
			data-row-error={row.hasError ? "true" : undefined}
		>
			{row.renderRowBody ? (
				row.renderRowBody()
			) : (
				<>
					<div
						className={cn(
							"sticky left-0 z-10 flex items-center pr-2 border-r",
							SURFACE_BG[surface],
							indent,
							groupBorder,
						)}
					>
						{isCollapsible ? (
							<ExpansionChevron
								expanded={expanded}
								onToggle={() => dispatch({ type: "toggle_row", rowId: row.id })}
								label={row.label}
							/>
						) : (
							!(hideExpansionSpacer || row.hideExpansionSpacer) && (
								<span className="inline-block w-5" />
							)
						)}
						{row.labelLeading && (
							<span className="ml-2 flex items-center shrink-0">{row.labelLeading}</span>
						)}
						{row.renderLabel ? (
							row.renderLabel()
						) : (
							<span
								className={cn(
									"ml-2 text-sm text-primary flex-1 min-w-0 truncate",
									labelTextClasses,
								)}
							>
								{row.label}
							</span>
						)}
						{row.labelActions}
					</div>
					{cellContent}
				</>
			)}
		</div>
	);

	if (!hasChildren || !expanded) {
		return renderRow;
	}

	return (
		<Fragment>
			{renderRow}
			{(row.children ?? []).map((child) => (
				<DataGridRowComponent
					key={child.id}
					row={child}
					visibleColumns={visibleColumns}
					gridTemplate={gridTemplate}
					groupBoundaryColumnIds={groupBoundaryColumnIds}
					lastColumnId={lastColumnId}
					depth={depth + 1}
					gridLines={gridLines}
					labelIndent={labelIndent}
					hideExpansionSpacer={hideExpansionSpacer}
					rowHeight={rowHeight}
				/>
			))}
		</Fragment>
	);
}
